import React from 'react';
import { Icon } from '@/shared/icons';
import { Checkbox } from './Checkbox';

/**
 * ListTableHead — células de tabela de listagem CRUD.
 *
 * Cabeçalho (`<thead><tr>`): SortableHeadCell, PlainHeadCell, SelectAllHeadCell.
 * Corpo (`<tbody><tr>`): SelectRowCell — a célula do quadradinho da linha, que
 * fica aqui para nascer com a MESMA geometria do SelectAllHeadCell
 * (`pl-4 pr-1 py-3 w-11`); geometria diferente desalinha a coluna.
 *
 * Fonte-da-verdade extraída do padrão B majoritário (findings F43/F59 do
 * audit 2026-07-25): `th px-4 py-3 text-left text-xs font-bold
 * whitespace-nowrap`, cor `var(--theme-muted-text)`, chevron 14px SÓ na
 * coluna ativa (decisão do dono — Projetos migra), hover no ordenável
 * escurece pra `var(--theme-card-text)`.
 *
 * As células vivem dentro de `<thead><tr>` fornecido pelo caller:
 *
 * ```tsx
 * <thead>
 *   <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
 *     <SelectAllHeadCell checked={allSelected} indeterminate={someSelected} onToggle={toggleAll} data-testid="users-check-all" />
 *     <SortableHeadCell label="Nome" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
 *     <PlainHeadCell label="Equipes" />
 *     <PlainHeadCell className="w-14" />
 *   </tr>
 * </thead>
 * <tbody>
 *   <tr onClick={() => open(user.id)}>
 *     <SelectRowCell checked={isSelected(user.id)} onToggle={() => toggle(user.id)} data-testid={`user-row-check-${user.id}`} />
 *     …
 *   </tr>
 * </tbody>
 * ```
 */

type HeadAlign = 'left' | 'center' | 'right';

const ALIGN_CLASS: Record<HeadAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/** Classes base compartilhadas pelas células (densidade px-4 py-3 — decisão F59). */
const BASE_CLASS = 'px-4 py-3 text-xs font-bold whitespace-nowrap';

/* ─── SortableHeadCell (th ordenável) ───────────────────────── */

export interface SortableHeadCellProps<K extends string = string> {
  /** Rótulo da coluna */
  label: React.ReactNode;
  /** Chave de ordenação desta coluna */
  sortKey: K;
  /** Chave atualmente ativa na página (chevron só aparece quando bate com sortKey) */
  activeKey: K | null | undefined;
  /** Direção atual da ordenação */
  dir: 'asc' | 'desc';
  /** Callback de clique — a página decide alternar dir/trocar coluna */
  onSort: (key: K) => void;
  /** Alinhamento do texto (default: left) */
  align?: HeadAlign;
  /** Classes extras (ex.: largura fixa `w-40`, visibilidade responsiva) */
  className?: string;
  'data-testid'?: string;
}

export function SortableHeadCell<K extends string = string>({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  align = 'left',
  className,
  'data-testid': dataTestId,
}: SortableHeadCellProps<K>) {
  const active = activeKey === sortKey;
  return (
    <th
      scope="col"
      className={`${BASE_CLASS} ${ALIGN_CLASS[align]} cursor-pointer select-none ${className ?? ''}`}
      style={{ color: 'var(--theme-muted-text)', transition: 'color 0.15s' }}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort(sortKey)}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-card-text)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
      data-testid={dataTestId}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {/* decisão F59: chevron SÓ na coluna ativa */}
        {active && <Icon name={dir === 'asc' ? 'chevron-up' : 'chevron-down'} size={14} />}
      </span>
    </th>
  );
}

/* ─── PlainHeadCell (th não-ordenável) ──────────────────────── */

export interface PlainHeadCellProps {
  /** Rótulo da coluna (omitir para colunas vazias, ex.: coluna de ações) */
  label?: React.ReactNode;
  /** Alinhamento do texto (default: left) */
  align?: HeadAlign;
  /** Classes extras (ex.: largura fixa `w-14` da coluna de ações) */
  className?: string;
}

export const PlainHeadCell: React.FC<PlainHeadCellProps> = ({ label, align = 'left', className }) => (
  <th
    scope="col"
    className={`${BASE_CLASS} ${ALIGN_CLASS[align]} ${className ?? ''}`}
    style={{ color: 'var(--theme-muted-text)' }}
  >
    {label}
  </th>
);

PlainHeadCell.displayName = 'PlainHeadCell';

/* ─── SelectAllHeadCell (th com checkbox selecionar-todos) ──── */

export interface SelectAllHeadCellProps {
  /** Todos os itens da página estão selecionados */
  checked: boolean;
  /** Seleção parcial (alguns itens, não todos) */
  indeterminate?: boolean;
  /** Alterna a seleção de todos os itens da página */
  onToggle: () => void;
  'data-testid'?: string;
  /** Rótulo acessível do checkbox (default: "Selecionar todos") */
  ariaLabel?: string;
  /** Classes extras no th (default já traz `pl-4 pr-1 py-3 w-11`) */
  className?: string;
}

export const SelectAllHeadCell: React.FC<SelectAllHeadCellProps> = ({
  checked,
  indeterminate = false,
  onToggle,
  'data-testid': dataTestId,
  ariaLabel = 'Selecionar todos',
  className,
}) => (
  <th scope="col" className={`pl-4 pr-1 py-3 w-11 ${className ?? ''}`}>
    <Checkbox
      checked={checked}
      indeterminate={indeterminate}
      onChange={onToggle}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    />
  </th>
);

SelectAllHeadCell.displayName = 'SelectAllHeadCell';

/* ─── SelectRowCell (td com checkbox de seleção da linha) ───── */

export interface SelectRowCellProps {
  /** Esta linha está selecionada */
  checked: boolean;
  /** Alterna a seleção desta linha */
  onToggle: () => void;
  'data-testid'?: string;
  /** Rótulo acessível do checkbox (default: "Selecionar linha") */
  ariaLabel?: string;
  /** Classes extras no td (default já traz `pl-4 pr-1 py-3 w-11`) */
  className?: string;
}

/**
 * Célula de seleção da LINHA — par do `SelectAllHeadCell`, mesma geometria
 * (`pl-4 pr-1 py-3 w-11`) para a coluna do corpo bater com a do cabeçalho.
 *
 * O `stopPropagation` mora aqui, no `<td>`, e não tem como morar em outro
 * lugar: a `<tr>` das listagens tem `onClick` que abre o registro, e o
 * `Checkbox` compartilhado entrega só o booleano (`onChange: (checked:
 * boolean) => void`) — o evento não chega ao consumidor. Sem o freio na
 * célula, marcar o quadradinho abriria o registro e a seleção se perderia.
 * Vale também para o teclado: Espaço num checkbox dispara um `click` que
 * borbulha igual.
 */
export const SelectRowCell: React.FC<SelectRowCellProps> = ({
  checked,
  onToggle,
  'data-testid': dataTestId,
  ariaLabel = 'Selecionar linha',
  className,
}) => (
  <td
    className={`pl-4 pr-1 py-3 w-11 ${className ?? ''}`}
    onClick={(e) => e.stopPropagation()}
  >
    <Checkbox
      checked={checked}
      onChange={onToggle}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    />
  </td>
);

SelectRowCell.displayName = 'SelectRowCell';
